import { computed } from 'vue'
import { state } from '@/store'

/**
 * ============================================================================
 * useHeroCoverSwitcher.js - Gestione Immagine Hero Dinamica
 * ============================================================================
 * 
 * Composable Vue 3 per gestire il cambio dinamico dell'immagine di copertina
 * dell'hero section. Integrato con lo store globale per sincronizzare lo stato
 * tra i componenti (es. header menu hovering → cambio immagine hero).
 * 
 * FLUSSO:
 * 1. SiteHeader.vue (menu hover) → chiama setHeroCover(index)
 * 2. Store state.currentCover aggiornato
 * 3. SectionHero.vue osserva currentCover → immagine hero cambia
 * 4. Animazione smooth via v-transition su src img
 * 
 * STORE STATE USATO:
 * • state.currentCover: indice dell'immagine attuale (0-based)
 * • state.coverImages: array di oggetti immagine
 *   Es: [{ src: '/img1.jpg', alt: 'Alt 1' }, { src: '/img2.jpg', ... }]
 * 
 * UTILIZZO:
 * const { currentCover, setHeroCover, resetHeroCover } = useHeroCoverSwitcher()
 * 
 * <!-- In template: -->
 * <img :src="currentCover.src" :alt="currentCover.alt">
 * 
 * <!-- Dai click handler: -->
 * @click="setHeroCover(1)"  <!-- Cambia a immagine #1 -->
 * @click="resetHeroCover()" <!-- Torna a immagine #0 -->
 * ============================================================================
 */

/**
 * Composable per gestire il cover (immagine copertina) dell'hero
 * 
 * @returns {Object} Oggetto reattivo con:
 *   - currentCover: Computed ref con l'immagine attuale
 *   - setHeroCover: Function per cambiare immagine
 *   - resetHeroCover: Function per tornare all'immagine di default (#0)
 */
export function useHeroCoverSwitcher () {
  /**
   * Immagine di copertina attuale ricavata dallo store
   * 
   * LOGICA:
   * 1. Legge state.currentCover (default: 0 se null/undefined)
   * 2. Accede a state.coverImages[index]
   * 3. Fallback a {} se l'indice non esiste
   * 
   * @returns {Object} Oggetto con src, alt, e altre prop dell'immagine
   */
  const currentCover = computed(() => {
    const i = state.currentCover ?? 0
    return state.coverImages?.[i] ?? {}
  })

  /**
   * Cambia l'immagine di copertina dell'hero
   * 
   * Usato principalmente:
   * • Menu hover in SiteHeader.vue
   * • Click handler nei componenti
   * 
   * @param {number} index - Indice nell'array state.coverImages (0-based)
   * 
   * ESEMPIO:
   * setHeroCover(0) → mostra coverImages[0]
   * setHeroCover(1) → mostra coverImages[1]
   * setHeroCover()  → mostra coverImages[0] (default)
   */
  function setHeroCover (index = 0) {
    if (typeof index === 'number') state.currentCover = index
  }

  /**
   * Resetta l'immagine di copertina alla predefinita (indice 0)
   * 
   * Usato quando:
   * • Menu non è più hoverato
   * • Navigazione verso un'altra pagina
   * • Reset esplicito richiesto
   * 
   * ESEMPIO:
   * resetHeroCover() → torna a coverImages[0]
   */
  function resetHeroCover () {
    state.currentCover = 0
  }

  return { currentCover, setHeroCover, resetHeroCover }
}